import { useCallback, useEffect, useState } from "react";

import { apiClient } from "../lib/api";

/**
 * Monthly spend-cap state for the signed-in user.
 *
 * Mirrors `build_spend_cap_payload` in
 * charti-ai-api/app/services/spend_cap_service.py. Cents on the wire so this
 * matches how billing amounts are already carried elsewhere in the platform.
 */
export interface SpendCapState {
  cap_cents: number | null;
  spent_cents: number;
  remaining_cents: number | null;
  percent_used: number | null;
  /** Precomputed server-side — never re-derive it from cap vs spent here, or
   *  the banner can disagree with the gate that actually refuses sessions. */
  exceeded: boolean;
  source: string;
  /** ISO timestamp: first of next UTC month, when spend resets to zero. */
  resets_at: string;
}

/**
 * Tracks whether the user has spent their monthly allowance.
 *
 * Two update paths, because polling alone is too slow to explain a refusal
 * the user just experienced:
 *
 *  - the `spend-cap:reached` window event, dispatched by the axios
 *    interceptor the moment any request is refused with 402, flips this to
 *    capped instantly; and
 *  - a slow poll, so the banner CLEARS on its own when the month rolls over
 *    or an admin raises the cap, with no reload and no admin follow-up.
 *
 * The poll is deliberately lazy (15 min): the cap moves on a monthly cadence,
 * and the event path already covers the case that actually matters to a user
 * mid-shift.
 */
const POLL_INTERVAL_MS = 15 * 60 * 1000;

export function useSpendCap() {
  const [state, setState] = useState<SpendCapState | null>(null);

  const refresh = useCallback(async () => {
    try {
      const res = await apiClient.get<SpendCapState>("/api/auth/me/spend-cap");
      setState(res.data);
    } catch {
      // Never surface a failure here. This drives a supplementary banner; if
      // the check itself is broken the user should keep working, not be shown
      // an error about a limit they may not even have.
    }
  }, []);

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => void refresh(), POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [refresh]);

  useEffect(() => {
    // A 402 landed. Flip to capped immediately from the payload we were given
    // rather than waiting on a round trip, then reconcile in the background so
    // `source` and `percent_used` are populated for the banner copy.
    const onReached = (event: Event) => {
      const detail = (event as CustomEvent).detail ?? {};
      setState((prev) => ({
        cap_cents: detail.capCents ?? prev?.cap_cents ?? null,
        spent_cents: detail.spentCents ?? prev?.spent_cents ?? 0,
        remaining_cents: 0,
        percent_used: 100,
        exceeded: true,
        source: prev?.source ?? "unknown",
        resets_at: detail.resetsAt ?? prev?.resets_at ?? "",
      }));
      void refresh();
    };

    window.addEventListener("spend-cap:reached", onReached);
    return () => window.removeEventListener("spend-cap:reached", onReached);
  }, [refresh]);

  return { spendCap: state, refreshSpendCap: refresh };
}
